import isEmpty from "lodash/isEmpty";
import React, { useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { Button } from "../../../components/Button";
import { Flex } from "../../../components/Flex";
import { Input } from "../../../components/Input";
import { GoogleButton } from "../../../components/logins/GoogleButton";
import { AppLangs } from "../../../model/shared/enum/AppLangs.enum";
import { SignupDto } from "../../../model/user/dto/SignupDto";
import { LoginWithGoogleReturnPath } from "../../../model/user/types/LoginWithGoogleReturnPath.enum";
import { useAppDispatch } from "../../../redux/store";
import { userActions } from "../../../redux/user/user.slice";
import { TestIDs } from "../../../tests/types/testIds";
import { Spacing } from "../../../theme/Spacing";
import { AtiraToast } from "../../../utils/AtiraToast";
import { Regexes } from "../../../utils/String";
import { onSetTypedCRUDErrors } from "../../../utils/onSetTypedCRUDErrors";
import { savePendingSignup } from "./signupProgress";

// Returned by the API when this email already has an unverified signup in
// progress - the user just needs to go enter (or resend) the code.
const PENDING_VERIFICATION_ERROR = "server-error/auth.signup.pending-verification";

const StyledInput = styled(Input)`
  width: 23rem;
  height: 2.3rem;
  border: 1px solid ${(props) => props.theme.lightergray};
  padding: ${Spacing.s};
  transition: border 0.2s;

  &:focus {
    border: 1px solid #86b7fe !important;
  }
`;

const StyledButton = styled(Button)`
  height: 2.3rem;
  width: 100%;
  background-color: ${({ theme }) => theme.main};
  transition: background-color 0.2s;
  margin: 0;

  &:hover {
    background-color: ${(props) => props.theme.lightMain};
  }
`;

const StyledGoogleButton = styled(GoogleButton)`
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15) !important;
  height: 2.3rem;
  width: 100%;
  border: 1px solid ${(props) => props.theme.transparent};
  transition: all 0.2s;

  &:hover {
    border: 1px solid #c6c7c8;
    background-color: ${(props) => props.theme.darkSub};
  }
`;

const StyledHr = styled.hr`
  background-color: ${(props) => props.theme.darkerSub};
  height: 1px;
  border: 0;
  opacity: 1;
  width: 100%;
`;

type SignupEmailProps = {
  updateStep: VoidFunction;
};

export const SignupEmail: React.FC<SignupEmailProps> = ({ updateStep }) => {
  const { t, i18n } = useTranslation();

  const [signupLoading, setSignupLoading] = useState(false);

  const dispatch = useAppDispatch();

  const { control, handleSubmit, getValues, setError } =
    useFormContext<SignupDto>();

  const goToTokenStep = () => {
    savePendingSignup({
      name: getValues("name"),
      email: getValues("email"),
    });
    updateStep();
  };

  const onNext = async () => {
    try {
      setSignupLoading(true);

      // Carry the current UI language so the API stores it as the new user's
      // default settings.lang (opens the app in this language on first login).
      const currentLang = i18n.language?.toLowerCase();
      const lang = (Object.values(AppLangs) as string[]).includes(currentLang)
        ? (currentLang as AppLangs)
        : undefined;

      await dispatch(
        userActions.signup({ ...getValues(), ...(lang ? { lang } : {}) }),
      ).unwrap();
      AtiraToast.success(t("signup.verification_sent_message"));
      goToTokenStep();
    } catch (e: any) {
      console.log(e);

      // Already mid-signup with this email: not an error for the user - send
      // them to the code step, where resend handles a lapsed code.
      if (e?.response?.data === PENDING_VERIFICATION_ERROR) {
        goToTokenStep();
        return;
      }

      AtiraToast.apiError(e);
      onSetTypedCRUDErrors<SignupDto>(e, setError);
    } finally {
      setSignupLoading(false);
    }
  };

  return (
    <Flex
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      width={"100%"}
      gap="s"
    >
      <Controller
        name="name"
        rules={{
          required: {
            value: true,
            message: t("signup.error.user_name.invalid_characters"),
          },
          pattern: {
            value: Regexes.NAME_REGEX,
            message: t("signup.error.user_name.invalid_characters"),
          },
        }}
        control={control}
        render={({ field: { value, onChange }, formState: { errors } }) => (
          <StyledInput
            title={t("common.name")}
            type="name"
            value={value}
            placeholder={t("common.name")}
            onChange={onChange}
            id="name"
            valid={isEmpty(errors.name)}
            errorMessage={errors.name?.message || ""}
            required
            dataTestId={TestIDs.auth.signup.name}
          />
        )}
      />

      <Controller
        name="email"
        control={control}
        rules={{
          required: true,
          pattern: Regexes.EMAIL_REGEX,
        }}
        render={({ field: { value, onChange }, formState: { errors } }) => (
          <StyledInput
            title={t("common.email")}
            type="email"
            value={value}
            placeholder={t("common.email")}
            onChange={onChange}
            id="email"
            valid={isEmpty(errors.email)}
            errorMessage={errors.email?.message || t("signup.error.email")}
            required
            dataTestId={TestIDs.auth.signup.email}
          />
        )}
      />

      <Controller
        name="password"
        control={control}
        rules={{ required: true }}
        render={({ field: { value, onChange }, formState: { errors } }) => (
          <StyledInput
            title={t("common.password")}
            placeholder={t("common.password")}
            type="password"
            value={value}
            onChange={onChange}
            id="password"
            valid={isEmpty(errors.password)}
            required
            errorMessage={
              errors.password?.message || t("signup.error.password")
            }
            dataTestId={TestIDs.auth.signup.password}
          />
        )}
      />

      <Flex flexDirection="column" gap="m" width={"100%"} marginTop="m">
        <StyledButton
          loading={signupLoading}
          onClick={handleSubmit(onNext, (e) => {
            console.log("invalid form submission", e);
          })}
          title={t("common.signup")}
          dataTestId={TestIDs.auth.signup.submit}
        />

        <StyledHr />
        <StyledGoogleButton returnPath={LoginWithGoogleReturnPath.SIGNUP}>
          {t("sign_up_with_google")}
        </StyledGoogleButton>
        <StyledHr />
      </Flex>
    </Flex>
  );
};
